import { TestsOverview } from "../../components/templates/TestsOverview.js";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ChapterDto } from "@mmm/contracts";
import { activateChapter, archiveChapter, createChapter, updateChapter, deleteChapter } from "../../api/commands.js";
import { useConfirm } from "../../components/shell/ConfirmProvider.js";
import { downloadStudyNotes } from "../../api/files.js";
import { ListRow } from "../../components/molecules/ListRow.js";
import { ExportPreview } from "../../components/organisms/ExportPreview.js";
import { StudyNotesPreview } from "../../components/organisms/StudyNotesPreview.js";
import { MapPreviewCard } from "../../components/organisms/MapPreviewCard.js";
import { ViewModeToggle, type ViewMode } from "../../components/ui/ViewModeToggle.js";
import { usePersistedViewMode } from "../../components/ui/usePersistedViewMode.js";
import { IconButton } from "../../components/ui/IconButton.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { useSubjectContext } from "./useSubjectContext.js";
import { Badge, Button, ColorDot, EmptyState, Grid, Icon, type MessageKey, ProgressBar, useT } from "@mmm/ui";
type ChapterFilter = "active" | "all" | "archived";

const CHAPTER_FILTER_OPTIONS: Array<{ value: ChapterFilter; label: MessageKey }> = [
  { value: "active", label: "common.active" },
  { value: "all", label: "common.all" },
  { value: "archived", label: "common.archived" },
];

export function SubjectOverviewTab() {
  const { subject } = useSubjectContext();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const t = useT();
  const [draft, setDraft] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [chapterView, setChapterView] = usePersistedViewMode("subject-chapters", "grid" satisfies ViewMode);
  const [chapterFilter, setChapterFilter] = useState<ChapterFilter>("active");

  const doneCount = subject.checkItems.filter((c) => c.done).length;
  const totalCount = subject.checkItems.length;
  const progressPercent = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);
  const activeChapterCount = subject.chapters.filter((chapter) => chapter.status === "active").length;
  const archivedChapterCount = subject.chapters.length - activeChapterCount;
  const visibleChapters = subject.chapters
    .filter((chapter) => chapterFilter === "all" || chapter.status === chapterFilter)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const handleAddChapter = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    // The form's onSubmit invokes this without awaiting it — a failed create
    // is already toasted by commands.ts's run(); keep the draft so the user
    // doesn't lose what they typed.
    try {
      const updatedSubject = await createChapter({ subjectId: subject.id, title: draft.trim() });
      const createdChapter = updatedSubject.chapters.find(
        (chapter) => !subject.chapters.some((existingChapter) => existingChapter.id === chapter.id)
      );
      setDraft("");
      if (createdChapter) {
        navigate(`/subjects/${subject.id}/chapters/${createdChapter.id}`, {
          // Same back link as opening an existing chapter below: both are
          // reached from Overview, which is also the bare subject landing page.
          state: { backTo: `/subjects/${subject.id}/overview` },
        });
      }
    } catch {
      // Nothing left to do.
    }
  };

  const commitRename = async (chapter: ChapterDto) => {
    setRenamingId(null);
    if (!renameDraft.trim() || renameDraft.trim() === chapter.title) return;
    try {
      await updateChapter({ chapterId: chapter.id, title: renameDraft.trim() });
    } catch {
      // Already toasted — onBlur invokes this without awaiting it.
    }
  };

  const handleDeleteChapter = async (chapter: ChapterDto) => {
    const ok = await confirm({
      title: t("chapters.deleteChapter"),
      message: t("chapters.deleteChapterMessage", { title: chapter.title }),
      confirmLabel: t("chapters.deleteChapter"),
    });
    if (!ok) return;
    try {
      await deleteChapter({ chapterId: chapter.id });
    } catch {
      // Already toasted — the IconButton's onClick invokes this without awaiting it.
    }
  };

  const handleArchiveToggle = async (chapter: ChapterDto) => {
    try {
      if (chapter.status === "active") await archiveChapter({ chapterId: chapter.id });
      else await activateChapter({ chapterId: chapter.id });
    } catch {
      // Already toasted — the IconButton's onClick invokes this without awaiting it.
    }
  };

  const openChapter = (chapter: ChapterDto) => {
    navigate(`/subjects/${subject.id}/chapters/${chapter.id}`, {
      // Back returns to Overview, the tab this was opened from -- a bare
      // subject path now lands on Overview instead.
      state: { backTo: `/subjects/${subject.id}/overview` },
    });
  };

  return (
    <>
    <Grid minItemWidth={420} gap={18} className="mmm-subject-overview-grid">
      <section className="mmm-dashboard-panel">
        <div className="mmm-dashboard-panel-head">
          <div>
            <h2>{t("chapters.heading")}</h2>
            <p>{t("common.activeArchivedSummary", { active: activeChapterCount, archived: archivedChapterCount })}</p>
          </div>
          <div className="mmm-panel-actions">
            <SegmentedControl
              options={CHAPTER_FILTER_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }))}
              value={chapterFilter}
              onChange={setChapterFilter}
            />
            <ViewModeToggle value={chapterView} onChange={setChapterView} />
          </div>
        </div>

        <form onSubmit={handleAddChapter} className="mmm-entry-form">
          <span className="mmm-entry-form-icon">
            <Icon name="add" />
          </span>
          <input
            className="mmm-input"
            placeholder={t("chapters.namePlaceholder")}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="primary" disabled={!draft.trim()}>
            {t("chapters.addChapter")}
          </Button>
        </form>

        <div className={`mmm-chapter-list mmm-chapter-list--${chapterView}`}>
          {visibleChapters.length === 0 ? (
            <EmptyState
              variant="list"
              title={chapterFilter === "archived" ? t("chapters.noneArchived") : t("chapters.noneShown")}
              message={chapterFilter === "active" ? t("chapters.emptyActiveHint") : t("chapters.emptyFilterHint")}
            />
          ) : (
            visibleChapters.map((chapter) => (
              <ListRow
                key={chapter.id}
                variant={chapterView}
                className={`mmm-chapter-item mmm-chapter-item--${chapterView} mmm-chapter-item--clickable ${chapter.status === "archived" ? "is-archived" : ""}`.trim()}
                role="link"
                tabIndex={0}
                onClick={() => openChapter(chapter)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openChapter(chapter);
                  }
                }}
                leading={<ColorDot color={subject.color} size={chapterView === "list" ? "sm" : "lg"} square bordered className="mmm-chapter-swatch" />}
                title={
                  renamingId === chapter.id ? (
                    <input
                      autoFocus
                      className="mmm-input"
                      value={renameDraft}
                      onChange={(event) => setRenameDraft(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                      onBlur={() => commitRename(chapter)}
                    />
                  ) : (
                    <span className="mmm-chapter-title">{chapter.title}</span>
                  )
                }
                meta={
                  <span className="mmm-chapter-status">
                  <Badge tone={chapter.status === "active" ? "success" : "neutral"}>
                    {chapter.status === "active" ? t("common.active") : t("common.archived")}
                  </Badge>
                  </span>
                }
                trailing={
                  <div className="mmm-item-actions">
                    <IconButton
                      icon="edit"
                      label={t("common.renameItem", { title: chapter.title })}
                      className="mmm-row-action-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        setRenamingId(chapter.id);
                        setRenameDraft(chapter.title);
                      }}
                    />
                    <IconButton
                      icon={chapter.status === "active" ? "archive" : "restore"}
                      label={
                        chapter.status === "active"
                          ? t("common.archiveItem", { title: chapter.title })
                          : t("common.unarchiveItem", { title: chapter.title })
                      }
                      className="mmm-row-action-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleArchiveToggle(chapter);
                      }}
                    />
                    <ExportPreview
                      trigger={({ onClick, disabled, busy }) => (
                        <IconButton
                          icon="export"
                          label={t("common.exportItem", { title: chapter.title })}
                          className="mmm-row-action-btn"
                          disabled={disabled}
                          aria-busy={busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            onClick();
                          }}
                        />
                      )}
                      preview={<StudyNotesPreview subjectId={subject.id} chapterId={chapter.id} />}
                      onWord={() => downloadStudyNotes({ subjectId: subject.id, chapterId: chapter.id, format: "docx" })}
                      onPdf={() => downloadStudyNotes({ subjectId: subject.id, chapterId: chapter.id, format: "pdf" })}
                    />
                    <IconButton
                      icon="trash"
                      label={t("common.deleteItem", { title: chapter.title })}
                      className="mmm-row-action-btn mmm-row-action-btn--danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteChapter(chapter);
                      }}
                    />
                  </div>
                }
              />
            ))
          )}
        </div>
      </section>

      <div className="mmm-subject-overview-side">
      <section className="mmm-dashboard-panel">
        <div className="mmm-dashboard-panel-head">
          <div>
            <h2>{t("subjects.summary")}</h2>
            <p>{t("subjects.percentComplete", { percent: progressPercent })}</p>
          </div>
        </div>

        <p className="mmm-summary-stat">
          {t("subjects.tasksDoneStat", { done: doneCount, count: totalCount, percent: progressPercent })}
        </p>
        <ProgressBar value={progressPercent} />

        <div className="mmm-summary-actions">
          <Button variant="secondary" onClick={() => navigate(`/subjects/${subject.id}/tasks`)}>
            <Icon name="list" />{t("subjects.viewTasks")}
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/subjects/${subject.id}/calendar`)}>
            <Icon name="calendar" />{t("subjects.viewCalendar")}
          </Button>
        </div>

      </section>
      <MapPreviewCard
        title={subject.title}
        color={subject.color}
        mindMapId={subject.mindMaps.find((map) => map.chapterId === null)?.id}
        to={`/subjects/${subject.id}/map`}
      />
      </div>
    </Grid>
    <TestsOverview tests={subject.aiTests} subjectId={subject.id} />
    </>
  );
}
